import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TimelineChart } from "@/components/timeline/TimelineChart";
import { GROUPINGS, type TimelineGrouping } from "@/lib/timeline";
import { DEMO_PEOPLE_COUNT, demoTimelineTasks } from "@/lib/timeline-demo";

export const Route = createFileRoute("/_authenticated/timeline-example")({
  head: () => ({
    meta: [
      { title: "Timeline example — MyPriorities" },
      {
        name: "description",
        content:
          "A worked example of the delivery timeline: a team of seven with a full quarter of tasks, QC dates, publishing sign-off and SLA flags.",
      },
      { property: "og:title", content: "Timeline example — MyPriorities" },
      {
        property: "og:description",
        content: "See what a fully populated team Gantt looks like before your own data fills in.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TimelineExamplePage,
});

function TimelineExamplePage() {
  const [grouping, setGrouping] = useState<TimelineGrouping>("owner");
  const tasks = useMemo(() => demoTimelineTasks(), []);

  const outside = tasks.filter((t) => t.within_sla === false).length;
  const pending = tasks.filter((t) => t.commercial_signoff_needed).length;

  return (
    <AppShell
      title="Timeline example"
      subtitle={`Sample data only — ${DEMO_PEOPLE_COUNT} people, ${tasks.length} pieces of work across the quarter.`}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/team">
            <ArrowLeft className="size-4" /> Back to Team
          </Link>
        </Button>
        <Badge variant="outline">Example data</Badge>
        <Badge variant="outline">{outside} outside SLA</Badge>
        <Badge variant="outline">{pending} awaiting commercial sign off</Badge>
      </div>

      <TimelineChart
        tasks={tasks}
        grouping={grouping}
        onGroupingChange={setGrouping}
        names={{ pillars: {}, goals: {} }}
        groupings={GROUPINGS.filter((g) => g.value !== "pillar" && g.value !== "goal")}
      />

      <p className="mt-4 text-xs text-muted-foreground">
        Nothing here is saved to your account — it exists to show how a fully populated team
        timeline reads once start dates, due dates and internal deadlines are in place.
      </p>
    </AppShell>
  );
}
